<script setup lang="ts">
// Overrides @basecom-gmbh/pond/components/Cms/Element/CmsElementImageGallery.vue
import { breakpointsTailwind, useBreakpoints } from '@vueuse/core';

const props = defineProps<{
    element: CmsElementImageGallery;
}>();

const elementConfig = useCmsElementConfig(props.element);
const elementData = useCmsElementData(props.element);

const navigationArrows = elementConfig.getConfigValue('navigationArrows');
const displayMode = elementConfig.getConfigValue('displayMode');
const minHeight = elementConfig.getConfigValue('minHeight');
const galleryPosition = elementConfig.getConfigValue('galleryPosition');

const thumbnailSlidesPerView = 6;
const spaceBetween = 16;

const sliderItems = elementData.getData('sliderItems') ?? [];

const _breakpoints = useBreakpoints(breakpointsTailwind);
const mdAndLarger = _breakpoints.greaterOrEqual('md');

// IDS-84 - extract all files other than mime type image and make them available for other components
const downloads = ref([]);
const slides = ref(
    sliderItems.filter(slide => {
        if (slide.media.mimeType.includes('image')) return slide;

        downloads.value.push(slide);
        useState('productDownloads', () => downloads.value ?? []);
    }),
);
</script>

<template>
    <!-- IDS-84 - change slider styles-->
    <ClientOnly>
        <div
            class="flex max-h-[640px] gap-2"
            :class="galleryPosition === 'underneath' ? 'flex-col' : 'flex-row-reverse'"
        >
            <template v-if="slides.length > 0">
                <LayoutSlider
                    :classes="[
                        slides.length > 1 ? 'cursor-grab' : '',
                        galleryPosition === 'underneath' ? 'w-full h-full max-h-[640px]' : 'w-auto',
                    ]"
                    :navigation-dots="!mdAndLarger"
                    :navigation-arrows="
                        navigationArrows !== 'None' && navigationArrows !== 'Keine' && slides.length > 1
                    "
                    :thumbs-swiper="`.thumbnailRef-${element.id}`"
                >
                    <LayoutSliderSlide
                        v-for="slide in slides"
                        :key="slide.media.id"
                        :class="`aspect-square min-h-[${minHeight}] max-h-[640px]`"
                    >
                        <img
                            v-if="slide.media.url"
                            :src="slide.media.url"
                            :alt="slide.translated?.alt ?? $t('cms.element.imageAlt')"
                            class="h-full w-full border border-neutral-300 object-center aspect-square"
                            :class="'object-' + displayMode"
                        />

                        <template v-else>
                            <div class="w-full bg-gray-light">
                                <SharedImagePlaceholder :size="'lg'" />
                            </div>
                        </template>
                    </LayoutSliderSlide>
                </LayoutSlider>
                <LayoutSlider
                    v-if="slides.length > 1"
                    :classes="[
                        galleryPosition === 'underneath' ? 'w-full' : '',
                        slides.length > 1 ? 'cursor-grab' : '',
                        'max-w-full hidden md:block',
                    ]"
                    :navigation-dots="false"
                    :navigation-arrows="slides.length > thumbnailSlidesPerView"
                    :vertical-navigation="false"
                    :direction="galleryPosition === 'left' ? 'vertical' : 'horizontal'"
                    :space-between="spaceBetween"
                    :slides-per-view="thumbnailSlidesPerView"
                    :thumb-ref="`thumbnailRef-${element.id}`"
                >
                    <LayoutSliderSlide
                        v-for="slide in slides"
                        :key="slide.media.id"
                        class="group aspect-square !mr-2"
                    >
                        <img
                            v-if="slide.media.url"
                            :src="slide.media.url"
                            :alt="slide.translated?.alt ?? $t('cms.element.imageAlt')"
                            class="border border-neutral-400 object-contain object-center opacity-40 group-[.swiper-slide-thumb-active]:border-neutral-800 group-[.swiper-slide-thumb-active]:opacity-100 aspect-square"
                        />

                        <template v-else>
                            <div
                                class="w-full border-neutral-400 bg-gray-light opacity-40 group-[.swiper-slide-thumb-active]:border-neutral-800 group-[.swiper-slide-thumb-active]:opacity-100"
                            >
                                <SharedImagePlaceholder :size="'sm'" />
                            </div>
                        </template>
                    </LayoutSliderSlide>
                </LayoutSlider>
            </template>

            <template v-else>
                <div class="w-full bg-gray-light">
                    <SharedImagePlaceholder :size="'lg'" />
                </div>
            </template>
        </div>
    </ClientOnly>
</template>
